import * as React from 'react';

import { BodySkeleton } from '../card-skeleton/card-skeleton';
import { cn } from '../../lib/cn';

export interface ListCardProps {
	/** Contenuto dell'intestazione, piccola e maiuscola. */
	header: React.ReactNode;
	/** Contenuto allineato a destra dell'intestazione (es. un'azione). */
	headerAside?: React.ReactNode;
	/** Renderizzato sotto l'intestazione, sopra le righe (es. un form di aggiunta inline). */
	subheader?: React.ReactNode;
	/** Righe ({@link ListCardRow}) o altro contenuto della lista. */
	children: React.ReactNode;
}

/** Card che incornicia un elenco di righe con un'intestazione compatta maiuscola. */
const ListCard = ({ header, headerAside, subheader, children }: ListCardProps) => (
	<div className="overflow-hidden rounded-xl border border-border bg-card">
		<div className="flex items-center justify-between border-b border-border p-3">
			<div className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider text-text-muted [&_svg]:h-3.5 [&_svg]:w-3.5">
				{header}
			</div>
			{headerAside}
		</div>
		{subheader && <div className="border-b border-border bg-background/50 p-3">{subheader}</div>}
		{children}
	</div>
);
ListCard.displayName = 'ListCard';

const LEADING_TONE = {
	accent: 'text-accent',
	purple: 'text-accent-purple',
	success: 'text-accent-green',
	warn: 'text-warn',
	muted: 'text-text-muted',
} as const;

export interface ListCardRowProps {
	/** Icona o avatar a sinistra. */
	leading?: React.ReactNode;
	/** Se impostato, dimensiona e colora un'icona SVG nuda passata come `leading`. */
	leadingTone?: keyof typeof LEADING_TONE;
	/** Titolo della riga. */
	title: React.ReactNode;
	/** Testo secondario sotto il titolo. */
	subtitle?: React.ReactNode;
	/** Azioni allineate a destra. */
	actions?: React.ReactNode;
	/** Se true, mostra un feedback di hover ad accento (righe cliccabili). */
	hover?: boolean;
}

/** Singola riga dentro una {@link ListCard}. */
const ListCardRow = ({
	leading,
	leadingTone,
	title,
	subtitle,
	actions,
	hover,
}: ListCardRowProps) => (
	<div
		className={cn(
			'flex items-center gap-3 border-b border-border/50 px-4 py-3 transition-colors last:border-b-0',
			hover && 'hover:bg-accent/5',
		)}
	>
		{leadingTone ? (
			<span
				className={cn(
					'inline-flex shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5',
					LEADING_TONE[leadingTone],
				)}
			>
				{leading}
			</span>
		) : (
			leading
		)}
		<div className="min-w-0 flex-1">
			<div className="truncate text-xs font-semibold text-text">{title}</div>
			{subtitle && <div className="truncate text-[12px] text-text-muted">{subtitle}</div>}
		</div>
		{actions}
	</div>
);
ListCardRow.displayName = 'ListCardRow';

export interface ListCardRowSkeletonProps {
	/** Mostra un cerchio a sinistra al posto dell'icona/avatar. Default: true. */
	leading?: boolean;
	/** Mostra un blocco a destra al posto delle azioni. Default: true. */
	actions?: boolean;
}

/** Riga skeleton della stessa forma di {@link ListCardRow}: avatar, titolo, sottotitolo, azioni. */
const ListCardRowSkeleton = ({ leading = true, actions = true }: ListCardRowSkeletonProps) => (
	<div className="flex items-center gap-3 border-b border-border/50 px-4 py-3 last:border-b-0">
		{leading && <BodySkeleton className="h-6 w-6 shrink-0 rounded-full" />}
		<div className="min-w-0 flex-1 space-y-1.5">
			<BodySkeleton className="h-3 w-1/3" />
			<BodySkeleton className="h-2.5 w-1/2" />
		</div>
		{actions && <BodySkeleton className="h-6 w-20 shrink-0 rounded-md" />}
	</div>
);
ListCardRowSkeleton.displayName = 'ListCardRowSkeleton';

export interface ListCardSkeletonProps {
	/** Numero di righe placeholder. Default: 3. */
	rows?: number;
	/** Passato a ogni {@link ListCardRowSkeleton}. */
	leading?: boolean;
	/** Passato a ogni {@link ListCardRowSkeleton}. */
	actions?: boolean;
}

/**
 * Righe {@link ListCardRowSkeleton} pronte da passare come `children` di una
 * {@link ListCard} reale: l'intestazione (il titolo) resta quella vera — chi
 * la usa la passa già com'è, senza contare ancora niente — e annuncia comunque
 * il caricamento agli screen reader.
 */
const ListCardSkeleton = ({ rows = 3, leading, actions }: ListCardSkeletonProps) => (
	<div
		// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico (`div`), stesso pattern di PanelCardSkeleton/DistributionBar.
		role="status"
		aria-label="Caricamento…"
	>
		{Array.from({ length: rows }).map((_, i) => (
			<ListCardRowSkeleton key={i} leading={leading} actions={actions} />
		))}
	</div>
);
ListCardSkeleton.displayName = 'ListCardSkeleton';

export interface ListCardEmptyProps {
	/** Messaggio da mostrare quando la lista non ha elementi. */
	children: React.ReactNode;
}

/** Messaggio vuoto centrato dentro una {@link ListCard}. */
const ListCardEmpty = ({ children }: ListCardEmptyProps) => (
	<div className="px-4 py-5 text-center text-[13px] text-text-muted">{children}</div>
);
ListCardEmpty.displayName = 'ListCardEmpty';

const INITIAL_TONE = {
	accent: 'bg-accent/15 text-accent',
	purple: 'bg-accent-purple/15 text-accent-purple',
	success: 'bg-accent-green/15 text-accent-green',
	warn: 'bg-warn/15 text-warn',
} as const;

export interface InitialAvatarProps {
	/** Iniziale/i mostrate nell'avatar. */
	initial: React.ReactNode;
	/** Colore dell'avatar. Default: `purple`. */
	tone?: keyof typeof INITIAL_TONE;
}

/** Piccolo avatar circolare a iniziali. */
const InitialAvatar = ({ initial, tone = 'purple' }: InitialAvatarProps) => (
	<div
		className={cn(
			'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
			INITIAL_TONE[tone],
		)}
	>
		{initial}
	</div>
);
InitialAvatar.displayName = 'InitialAvatar';

export {
	InitialAvatar,
	ListCard,
	ListCardEmpty,
	ListCardRow,
	ListCardRowSkeleton,
	ListCardSkeleton,
};
